import React, {memo, useEffect} from "react"
import {useTranslation} from "react-i18next";


import FormSection from "../../../components/FormSection";
import {formikPropertyChanged, generateMeasurementKey} from "./index";
import CollectionsTabList from "./CollectionsTabList";
import Model23MeasureField from "./Model23MeasureField";
import MeasurementsList from "./MeasurementsList";


const RamTests = memo(({formik, emptyTest, emptyMeasure, inputDisabled}) => {
    const {t} = useTranslation(['models23', 'enums']);

    const onTestsUpdate = (tests) => {
        formik.setFieldValue('ramTests', tests);
    }

    const onMeasurementsUpdate = (testIndex, measurements) => {
        formik.setFieldValue(`ramTests[${testIndex}].measurements`, measurements);
    }

    const createEmptyMeasure = (prevMeasurement) => {
        let weightHammer = 0;
        let weightTube = emptyMeasure.weightTube ?? 10;
        let depth = 0;
        if (prevMeasurement) {
            weightTube = prevMeasurement.weightTube;
            depth = prevMeasurement.depth;
            weightHammer = 10;
        }
        return {...emptyMeasure, weightHammer, weightTube, depth, _key: generateMeasurementKey(),};
    }

    const renderTest = (testIndex) => {
        return <RamTest formik={formik}
                        testIndex={testIndex}
                        createEmptyMeasure={createEmptyMeasure}
                        onMeasurementsUpdate={(measurements) => onMeasurementsUpdate(testIndex, measurements)}
                        inputDisabled={inputDisabled}/>
    }

    return <FormSection title={t('model23Form.ramTests')}
                        openDefault={false}
                        error={Boolean(formik.errors.ramTests) && formik.touched.ramTests}>
        <CollectionsTabList collections={formik.values.ramTests}
                            renderCollection={renderTest}
                            emptyCollection={emptyTest}
                            onCollectionsUpdate={onTestsUpdate}
                            collectionType="test"
                            inputDisabled={inputDisabled}/>
    </FormSection>
}, (prevProps, nextProps) => formikPropertyChanged(prevProps, nextProps, 'ramTests', true));


const RamTest = ({formik, testIndex, createEmptyMeasure, onMeasurementsUpdate, inputDisabled}) => {
    const {t} = useTranslation(['models23']);

    const test = formik.values.ramTests[testIndex];

    const headers = [
        t('model23Form.ramTest.weightTube'),
        t('model23Form.ramTest.weightHammer'),
        t('model23Form.ramTest.dropsNumber'),
        t('model23Form.ramTest.dropHeight'),
        t('model23Form.ramTest.depth'),
        t('model23Form.ramTest.deltaHeight'),
        t('model23Form.ramTest.hardness'),
    ]

    const renderMeasure = (row, index) => {
        return <Measure test={test} testIndex={testIndex} formik={formik} index={index} inputDisabled={inputDisabled}/>
    }

    return <MeasurementsList headers={headers}
                             measurements={test.measurements}
                             createEmptyMeasure={createEmptyMeasure}
                             onMeasurementsUpdate={onMeasurementsUpdate}
                             renderMeasure={renderMeasure}
                             inputDisabled={inputDisabled}/>
}

const getDeltaHeight = (measurement, prevMeasurement) => {
    let prevDepth = 0;
    const depth = measurement.depth;
    if(!Number.isFinite(depth)){
        return null;
    }
    if(prevMeasurement !== null && prevMeasurement !== undefined) {
        prevDepth = prevMeasurement.depth;
    }
    if(!Number.isFinite(prevDepth)){
        return null;
    }
    const delta = depth - prevDepth;
    if(delta < 0) {
        return null;
    }
    return delta;
}

const getHardness = (measurement) => {
    const {weightHammer, weightTube, dropsNumber, dropHeight, deltaHeight} = measurement;
    let hardness = undefined;

    if (!Number.isFinite(weightHammer) || !Number.isFinite(deltaHeight) || !Number.isFinite(weightTube) || !Number.isFinite(dropsNumber) || !Number.isFinite(dropHeight)) {
        return hardness;
    }

    if (deltaHeight === 0) {
        hardness = (weightTube + weightHammer);
    } else{
        hardness = ( weightHammer * dropsNumber * dropHeight * 1.0) / deltaHeight + weightTube + weightHammer;
    }

    return hardness.toFixed(0);
}

const getHammerWeight = (measurement, prevMeasurement) => {
    if(!Number.isFinite(measurement?.weightTube) && !Number.isFinite(prevMeasurement?.weightTube)){
        return measurement.weightHammer;
    }
    return measurement?.weightTube === prevMeasurement?.weightTube ? 10 : 0;
}

const Measure = ({test, testIndex, formik, index, inputDisabled}) => {
    const testPrefix = `ramTests[${testIndex}]`

    const measurement = test.measurements[index];
    const prevMeasurement = index > 0 ? test.measurements[index - 1] : null;

    // Every time depth, weightHammer, weightTube, dropsNumber, dropHeight (or previous values) changes
    // we recalculate deltaHeight and hardness
    // TODO do not use effect because they overwrite persistent values without user interaction
    useEffect(() => {
        const newDeltaHeight = getDeltaHeight(measurement, prevMeasurement);
        const newHardness = getHardness({...measurement, deltaHeight: newDeltaHeight});

        if(measurement.deltaHeight !== newDeltaHeight || measurement.hardness !== newHardness){
            formik.setFieldValue(`${testPrefix}.measurements[${index}]`,
                {...measurement, deltaHeight: newDeltaHeight, hardness: newHardness});
        }
    }, [measurement?.depth, measurement?.weightHammer, measurement?.weightTube, measurement?.dropsNumber, measurement?.dropHeight, measurement?.weightTube, prevMeasurement?.depth, prevMeasurement?.weightTube]);

    // When weightTube changes we recalculate weightHammer and hardness
    const handleWeightTubeChange = (event) => {
        const newWeightTube = Number(event.target.value);
        let newMeasurement = {...measurement, weightTube: newWeightTube};

        const newHammerWeight = getHammerWeight(newMeasurement, prevMeasurement);
        newMeasurement = {...newMeasurement, weightHammer: newHammerWeight};

        const newHardness = getHardness(newMeasurement);
        newMeasurement = {...newMeasurement, hardness: newHardness};

        formik.setFieldValue(`${testPrefix}.measurements[${index}]`, newMeasurement);
    }

    // If the user changes deltaHeight, we recalculate hardness
    const handleDeltaChange = (event) => {
        const newDeltaHeight = Number(event.target.value);
        let newMeasurement = {...measurement, deltaHeight: newDeltaHeight};

        const newHardness = getHardness(newMeasurement);
        newMeasurement = {...newMeasurement, hardness: newHardness};

        formik.setFieldValue(`${testPrefix}.measurements[${index}]`, newMeasurement);
    }

    return <>
        <Model23MeasureField formik={formik}
                             onChange={handleWeightTubeChange}
                             label=""
                             name={`${testPrefix}.measurements[${index}].weightTube`}
                             unitOfMeasure="N"
                             type="number"
                             disabled={inputDisabled}/>
        <Model23MeasureField formik={formik}
                             label=""
                             name={`${testPrefix}.measurements[${index}].weightHammer`}
                             unitOfMeasure="N"
                             type="number"
                             disabled={inputDisabled}/>
        <Model23MeasureField formik={formik}
                             label=""
                             name={`${testPrefix}.measurements[${index}].dropsNumber`}
                             unitOfMeasure=""
                             type="number"
                             disabled={inputDisabled}/>
        <Model23MeasureField formik={formik}
                             label=""
                             name={`${testPrefix}.measurements[${index}].dropHeight`}
                             unitOfMeasure="cm"
                             type="number"
                             disabled={inputDisabled}/>
        <Model23MeasureField formik={formik}
                             label=""
                             name={`${testPrefix}.measurements[${index}].depth`}
                             unitOfMeasure="cm"
                             type="number"
                             disabled={inputDisabled}/>
        <Model23MeasureField formik={formik}
                             onChange={handleDeltaChange}
                             label=""
                             name={`${testPrefix}.measurements[${index}].deltaHeight`}
                             unitOfMeasure="cm"
                             type="number"
                             disabled={inputDisabled}/>
        <Model23MeasureField formik={formik}
                             label=""
                             name={`${testPrefix}.measurements[${index}].hardness`}
                             unitOfMeasure="N"
                             type="number"
                             disabled={inputDisabled}/>
    </>
}

export default RamTests;